
import React, { useState, useEffect } from "react";
import ModeSelector from "../modes/ModeSelector";
import FastMode from "../modes/FastMode";
import SlicerSelection from "../tutorial/SlicerSelection";
import MethodSelection from "../tutorial/MethodSelection";
import CalibrationInstructions from "../tutorial/CalibrationInstructions";
import InitialFlowInput from "../tutorial/InitialFlowInput";
import ModifierInput from "../tutorial/ModifierInput";
import ResultsView from "../tutorial/ResultsView";

interface FlowCalculatorStepsProps {
  method: "classic" | "yolo";
}

const FlowCalculatorSteps = ({ method }: FlowCalculatorStepsProps) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [mode, setMode] = useState<"fast" | "tutorial" | null>(null);
  const [selectedSlicer, setSelectedSlicer] = useState<"creality" | "orca" | "anycubic">("orca");
  const [selectedMethod, setSelectedMethod] = useState<"classic" | "yolo">(method);
  const [initialFlowRatio, setInitialFlowRatio] = useState("");
  const [modifier, setModifier] = useState("");
  const [newFlowRatio, setNewFlowRatio] = useState("");
  const [currentPass, setCurrentPass] = useState(1);
  const [previousFlowRatio, setPreviousFlowRatio] = useState("");
  const [previousFlowRatioOriginal, setPreviousFlowRatioOriginal] = useState("");
  const [previousFlowRatioFormatted, setPreviousFlowRatioFormatted] = useState("");

  // Normalize decimal separators to standard decimal point
  const normalizeDecimalSeparator = (value: string) => {
    // Replace comma, apostrophe, and Arabic decimal separator with decimal point
    return value.replace(/[,''٫]/g, '.');
  };

  // Detect the decimal separator used in the input and format the result accordingly
  const formatWithOriginalSeparator = (value: string, originalInput: string) => {
    if (!value) return value;
    
    // Find which separator was used in the original input
    let usedSeparator = '.';
    if (originalInput.includes(',')) usedSeparator = ',';
    else if (originalInput.includes("'")) usedSeparator = "'";
    else if (originalInput.includes('٫')) usedSeparator = '٫';
    
    // If the original used a different separator, replace the decimal point
    if (usedSeparator !== '.') {
      return value.replace('.', usedSeparator);
    }
    
    return value;
  };

  // Format the flow ratio to remove trailing zeros and preserve original separator
  const formatFlowRatio = (value: string, originalInput: string) => {
    const normalizedValue = normalizeDecimalSeparator(value);
    const num = parseFloat(normalizedValue);
    if (isNaN(num)) return value;
    
    const formattedNum = num.toString();
    return formatWithOriginalSeparator(formattedNum, originalInput);
  };

  // Reset all variables function
  const resetAllVariables = () => {
    setSelectedSlicer("orca");
    setSelectedMethod(method);
    setInitialFlowRatio("");
    setModifier("");
    setNewFlowRatio("");
    setCurrentPass(1);
    setPreviousFlowRatio("");
    setPreviousFlowRatioOriginal("");
    setPreviousFlowRatioFormatted("");
    // Clear localStorage
    localStorage.removeItem(`flowCalculator_${method}_initialFlow`);
    localStorage.removeItem(`flowCalculator_${method}_modifier`);
    localStorage.removeItem(`flowCalculator_${method}_currentStep`);
    localStorage.removeItem(`flowCalculator_classic_initialFlow`);
    localStorage.removeItem(`flowCalculator_classic_modifier`);
    localStorage.removeItem(`flowCalculator_classic_currentStep`);
    localStorage.removeItem(`flowCalculator_yolo_initialFlow`);
    localStorage.removeItem(`flowCalculator_yolo_modifier`);
    localStorage.removeItem(`flowCalculator_yolo_currentStep`);
  };

  // Reset calculator on load
  useEffect(() => {
    setCurrentStep(1);
    setMode(null);
    resetAllVariables();
  }, [method]);

  // Calculate total steps based on mode and slicer
  const getTotalSteps = () => {
    if (mode === "fast") return 1;
    if (mode === "tutorial") {
      return selectedSlicer === "creality" ? 5 : 6;
    }
    return 1;
  };

  // Calculate new flow ratio with proper decimal separator handling
  useEffect(() => {
    if (initialFlowRatio && modifier) {
      const normalizedInitial = normalizeDecimalSeparator(initialFlowRatio);
      const normalizedModifier = normalizeDecimalSeparator(modifier);
      
      const initial = parseFloat(normalizedInitial);
      const mod = parseFloat(normalizedModifier);
      
      if (!isNaN(initial) && !isNaN(mod)) {
        let result;
        if (selectedMethod === "classic") {
          result = initial * (100 + mod) / 100;
        } else {
          // YOLO mode: FlowRatio_old ± modifier
          result = initial + mod;
        }
        setNewFlowRatio(result.toFixed(4));
      }
    } else {
      setNewFlowRatio("");
    }
  }, [initialFlowRatio, modifier, selectedMethod]);

  const startOver = () => {
    setCurrentStep(1);
    setMode(null);
    resetAllVariables();
  };

  const goToTutorial = () => {
    // Reset everything when switching to tutorial mode
    resetAllVariables();
    setMode("tutorial");
    setCurrentStep(1);
  };

  const handleBackToModeSelection = () => {
    // Reset all variables when going back to mode selection
    resetAllVariables();
    setCurrentStep(1);
    setMode(null);
  };

  const handleNextPass = () => {
    setPreviousFlowRatio(newFlowRatio);
    setPreviousFlowRatioOriginal(initialFlowRatio);
    // Store the formatted calculated result for Pass 2 display
    setPreviousFlowRatioFormatted(formatFlowRatio(newFlowRatio, initialFlowRatio));
    setCurrentPass(2);
    setInitialFlowRatio("");
    setModifier("");
    setNewFlowRatio("");
    // Go back to calibration instructions
    if (selectedSlicer === "creality") {
      setCurrentStep(2);
    } else {
      setCurrentStep(3);
    }
  };

  // Fast Mode Component
  if (mode === "fast") {
    return (
      <FastMode
        selectedMethod={selectedMethod}
        onMethodChange={setSelectedMethod}
        initialFlowRatio={initialFlowRatio}
        onInitialFlowRatioChange={setInitialFlowRatio}
        modifier={modifier}
        onModifierChange={setModifier}
        newFlowRatio={newFlowRatio}
        onGoToTutorial={goToTutorial}
      />
    );
  }

  return (
    <div className="space-y-4">
      {/* Step 1: Mode Selection */}
      {currentStep === 1 && mode === null && (
        <ModeSelector
          onSelectFastMode={() => setMode("fast")}
          onSelectTutorialMode={goToTutorial}
        />
      )}

      {/* Tutorial Step 1: Slicer Selection */}
      {currentStep === 1 && mode === "tutorial" && (
        <SlicerSelection
          selectedSlicer={selectedSlicer}
          onSlicerChange={setSelectedSlicer}
          onNext={() => setCurrentStep(2)}
          onPrevious={handleBackToModeSelection}
        />
      )}

      {/* Tutorial Step 2: Method Selection (Orca/Anycubic only) or Calibration Instructions (Creality) */}
      {currentStep === 2 && mode === "tutorial" && (
        <>
          {selectedSlicer === "creality" ? (
            <CalibrationInstructions
              selectedSlicer={selectedSlicer}
              selectedMethod={selectedMethod}
              onNext={() => setCurrentStep(3)}
              onPrevious={handleBackToModeSelection}
              currentPass={currentPass}
              newFlowRatio={currentPass === 2 ? previousFlowRatioFormatted : undefined}
              previousFlowRatioOriginal={currentPass === 2 ? previousFlowRatioOriginal : undefined}
            />
          ) : (
            <MethodSelection
              selectedMethod={selectedMethod}
              onMethodChange={setSelectedMethod}
              onNext={() => setCurrentStep(3)}
              onPrevious={handleBackToModeSelection}
              currentPass={currentPass}
            />
          )}
        </>
      )}

      {/* Tutorial Step 3: Orca/Anycubic Calibration Instructions (Orca/Anycubic only) */}
      {currentStep === 3 && mode === "tutorial" && (selectedSlicer === "orca" || selectedSlicer === "anycubic") && (
        <CalibrationInstructions
          selectedSlicer={selectedSlicer}
          selectedMethod={selectedMethod}
          onNext={() => setCurrentStep(4)}
          onPrevious={() => setCurrentStep(2)}
          currentPass={currentPass}
          newFlowRatio={currentPass === 2 ? previousFlowRatioFormatted : undefined}
          previousFlowRatioOriginal={currentPass === 2 ? previousFlowRatioOriginal : undefined}
        />
      )}

      {/* Initial Flow Ratio Step */}
      {mode === "tutorial" && ((currentStep === 3 && selectedSlicer === "creality") || (currentStep === 4 && (selectedSlicer === "orca" || selectedSlicer === "anycubic"))) && (
        <InitialFlowInput
          selectedSlicer={selectedSlicer}
          initialFlowRatio={initialFlowRatio}
          onInitialFlowRatioChange={setInitialFlowRatio}
          onNext={() => setCurrentStep(currentStep + 1)}
          onPrevious={() => setCurrentStep(selectedSlicer === "creality" ? 2 : 3)}
          currentPass={currentPass}
          previousFlowRatio={currentPass === 2 ? previousFlowRatio : undefined}
          previousFlowRatioOriginal={currentPass === 2 ? previousFlowRatioOriginal : undefined}
          previousFlowRatioFormatted={currentPass === 2 ? previousFlowRatioFormatted : undefined}
        />
      )}

      {/* Modifier Step */}
      {mode === "tutorial" && ((currentStep === 4 && selectedSlicer === "creality") || (currentStep === 5 && (selectedSlicer === "orca" || selectedSlicer === "anycubic"))) && (
        <ModifierInput
          selectedMethod={selectedMethod}
          modifier={modifier}
          onModifierChange={setModifier}
          onNext={() => setCurrentStep(currentStep + 1)}
          onPrevious={() => setCurrentStep(currentStep - 1)}
          currentPass={currentPass}
        />
      )}

      {/* Results Step */}
      {mode === "tutorial" && ((currentStep === 5 && selectedSlicer === "creality") || (currentStep === 6 && (selectedSlicer === "orca" || selectedSlicer === "anycubic"))) && newFlowRatio && (
        <ResultsView
          selectedSlicer={selectedSlicer}
          selectedMethod={selectedMethod}
          initialFlowRatio={initialFlowRatio}
          modifier={modifier}
          newFlowRatio={newFlowRatio}
          onPrevious={() => setCurrentStep(currentStep - 1)}
          onStartOver={startOver}
          currentPass={currentPass}
          onNextPass={handleNextPass}
        />
      )}
    </div>
  );
};

export default FlowCalculatorSteps;
